import React, { useState } from 'react';

export const Spinner = ({ size = 20, color = 'white' }) => (
  <div className={`spinner spinner-${color}`} style={{ width:size, height:size }} />
);

export const Alert = ({ type = 'error', children, onClose }) => {
  const icons = { error:'⚠️', success:'✅', warning:'⚡', info:'ℹ️' };
  return (
    <div className={`alert alert-${type}`}>
      <span style={{ flexShrink:0, marginTop:1 }}>{icons[type]}</span>
      <span style={{ flex:1 }}>{children}</span>
      {onClose && <button onClick={onClose} style={{ background:'none',border:'none',cursor:'pointer',fontSize:16,opacity:0.45,lineHeight:1,padding:2,flexShrink:0 }}>✕</button>}
    </div>
  );
};

export const Badge = ({ children, type = 'blue' }) => <span className={`badge badge-${type}`}>{children}</span>;

export const StatusBadge = ({ status }) => {
  const map = {
    active:['success','✅ Active'], pending:['warning','⏳ Pending'],
    rejected:['error','❌ Rejected'], suspended:['error','🔒 Suspended'],
    approved:['success','✅ Approved'],
    Easy:['success','Easy'], Medium:['warning','Medium'], Hard:['error','Hard'],
  };
  const [t, l] = map[status] || ['blue', status];
  return <Badge type={t}>{l}</Badge>;
};

export const Modal = ({ open, onClose, title, children, footer, size = 560 }) => {
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:size }}>
        <div className="modal-header">
          <h3 className="modal-title">{title}</h3>
          <button onClick={onClose} style={{ background:'none',border:'none',cursor:'pointer',fontSize:20,color:'#aaa',lineHeight:1,padding:4,borderRadius:6,transition:'all 0.2s' }}
            onMouseEnter={e=>e.currentTarget.style.background='var(--bg)'}
            onMouseLeave={e=>e.currentTarget.style.background='none'}>✕</button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
};

export const StatCard = ({ icon, label, value, sub, color = 'blue' }) => {
  const grad = { blue:'linear-gradient(90deg,#1A3C8F,#2451b3)', gold:'linear-gradient(90deg,#F5A623,#c8841a)', green:'linear-gradient(90deg,#28A745,#1e7e34)', red:'linear-gradient(90deg,#dc3545,#c82333)' };
  const bg = { blue:'rgba(26,60,143,0.07)', gold:'rgba(245,166,35,0.08)', green:'rgba(40,167,69,0.07)', red:'rgba(220,53,69,0.07)' };
  const tc = { blue:'var(--blue)', gold:'var(--gold-dark)', green:'var(--success)', red:'var(--error)' };
  return (
    <div className="card" style={{ overflow:'hidden', position:'relative', transition:'all 0.25s' }}
      onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-3px)';e.currentTarget.style.boxShadow='var(--shadow-md)';}}
      onMouseLeave={e=>{e.currentTarget.style.transform='translateY(0)';e.currentTarget.style.boxShadow='var(--shadow-sm)';}}>
      <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:grad[color]||grad.blue }} />
      <div className="card-body" style={{ paddingTop:22, display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:10, fontWeight:700, color:'var(--muted)', letterSpacing:'1px', textTransform:'uppercase', marginBottom:8 }}>{label}</div>
          <div style={{ fontFamily:'var(--font-heading)', fontSize:32, fontWeight:900, color:'var(--dark)', lineHeight:1, marginBottom:4 }}>{value}</div>
          {sub && <div style={{ fontSize:11, color:'var(--muted)' }}>{sub}</div>}
        </div>
        <div style={{ width:46, height:46, borderRadius:12, background:bg[color]||bg.blue, display:'flex', alignItems:'center', justifyContent:'center', fontSize:21, flexShrink:0 }}>{icon}</div>
      </div>
    </div>
  );
};

export const EmptyState = ({ icon = '📭', title = 'No Data', desc = '', action }) => (
  <div style={{ textAlign:'center', padding:'44px 20px' }}>
    <div style={{ fontSize:52, marginBottom:14 }}>{icon}</div>
    <h3 style={{ fontFamily:'var(--font-heading)', fontSize:18, marginBottom:7, color:'var(--dark)' }}>{title}</h3>
    {desc && <p style={{ color:'var(--muted)', fontSize:13, marginBottom:action ? 18 : 0 }}>{desc}</p>}
    {action}
  </div>
);

export const Tabs = ({ tabs, active, onChange }) => (
  <div style={{ display:'flex', gap:4, background:'var(--bg2)', borderRadius:13, padding:4, overflowX:'auto' }}>
    {tabs.map(t => (
      <button key={t.value} onClick={() => onChange(t.value)} style={{ flex:'0 0 auto', padding:'9px 16px', borderRadius:10, border:'none', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:13, fontWeight:600, transition:'all 0.22s', whiteSpace:'nowrap', background: active === t.value ? '#fff' : 'transparent', color: active === t.value ? 'var(--blue)' : 'var(--muted)', boxShadow: active === t.value ? 'var(--shadow-sm)' : 'none' }}>
        {t.icon && <span style={{ marginRight:5 }}>{t.icon}</span>}{t.label}
      </button>
    ))}
  </div>
);

export const Pagination = ({ page, pages, onPage }) => {
  if (pages <= 1) return null;
  const nums = [];
  for (let i = Math.max(1, page - 2); i <= Math.min(pages, page + 2); i++) nums.push(i);
  return (
    <div style={{ display:'flex', alignItems:'center', gap:5, justifyContent:'center', marginTop:22, flexWrap:'wrap' }}>
      <button className="btn btn-outline btn-sm" onClick={() => onPage(page-1)} disabled={page<=1}>← Prev</button>
      {nums[0] > 1 && <><button className="btn btn-outline btn-sm" onClick={()=>onPage(1)}>1</button>{nums[0]>2&&<span style={{color:'var(--muted)',padding:'0 4px'}}>…</span>}</>}
      {nums.map(n=><button key={n} className={`btn btn-sm ${n===page?'btn-primary':'btn-outline'}`} onClick={()=>onPage(n)}>{n}</button>)}
      {nums[nums.length-1]<pages&&<><span style={{color:'var(--muted)',padding:'0 4px'}}>…</span><button className="btn btn-outline btn-sm" onClick={()=>onPage(pages)}>{pages}</button></>}
      <button className="btn btn-outline btn-sm" onClick={() => onPage(page+1)} disabled={page>=pages}>Next →</button>
    </div>
  );
};

export const PasswordInput = ({ label, error, required, ...props }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="form-group">
      {label && <label className="form-label">{label}{required && <span className="req"> *</span>}</label>}
      <div className="input-wrap">
        <span className="input-icon">🔒</span>
        <input type={show ? 'text' : 'password'} className={`form-input${error ? ' error' : ''}`} style={{ paddingRight:44 }} {...props} />
        <button type="button" onClick={() => setShow(s => !s)} style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', fontSize:15, color:'#bbb', padding:2, lineHeight:1 }}>
          {show ? '🙈' : '👁️'}
        </button>
      </div>
      {error && <span className="field-error">{error}</span>}
    </div>
  );
};

export const ProgressBar = ({ value, max=100, color='blue', label, showPct=true }) => {
  const pct = Math.min(100, Math.round((value/max)*100));
  const grad = { blue:'linear-gradient(90deg,#1A3C8F,#2451b3)', gold:'linear-gradient(90deg,#F5A623,#c8841a)', green:'linear-gradient(90deg,#28a745,#1e7e34)', red:'linear-gradient(90deg,#dc3545,#c82333)' };
  return (
    <div>
      {(label||showPct) && (
        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
          {label && <span style={{ fontSize:12, color:'var(--muted)' }}>{label}</span>}
          {showPct && <span style={{ fontSize:12, fontWeight:700, color:'var(--dark)' }}>{pct}%</span>}
        </div>
      )}
      <div style={{ height:7, background:'var(--border)', borderRadius:4, overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${pct}%`, background:grad[color]||grad.blue, borderRadius:4, transition:'width 1.2s ease' }} />
      </div>
    </div>
  );
};
